import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";

const SESSION_DISMISS_KEY = "ivoliq_avv_banner_dismissed_session";

export default function AVVConsentBanner() {
  const { user, loading, refreshUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Session-level dismiss: remember until tab reload/next login
    if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") {
      setDismissed(true);
    }
  }, []);

  // Gate conditions: wait for auth, only show to authenticated users
  // who never accepted OR whose accepted version is stale.
  if (loading || !user) return null;
  const CURRENT_AVV_VERSION = "2.0";
  const accepted = Boolean(user.avv_accepted_at) && user.avv_version === CURRENT_AVV_VERSION;
  if (accepted) return null;
  if (dismissed) return null;
  // Overlay-Priorität (Juni 2026, Paket A): erst nach der Cookie-Entscheidung zeigen
  let cookieDecided = false;
  try { cookieDecided = Boolean(localStorage.getItem('ivoliq_cookie_consent')); } catch { /* noop */ }
  if (!cookieDecided) return null;

  const handleAccept = async () => {
    setSaving(true);
    try {
      await api.post("/legal/accept-avv");
      toast.success("Danke! AVV akzeptiert.");
      await refreshUser();
    } catch (e) {
      toast.error("Konnte nicht gespeichert werden. Bitte erneut versuchen.");
    } finally {
      setSaving(false);
    }
  };

  const handleLater = () => {
    sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] max-w-md w-[calc(100vw-2rem)] sm:w-auto animate-in slide-in-from-bottom-5 duration-300"
      data-testid="avv-consent-banner"
    >
      <div className="bg-white border border-blue-200 rounded-xl shadow-lg p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              Neue AVV – bitte kurz bestätigen
            </h3>
            <p className="text-xs text-gray-600 leading-relaxed mb-3">
              Nach Art. 28 DSGVO benötigst du als Verantwortlicher eine AVV mit ivoliq. Diese ist
              jetzt verfügbar – bitte einmalig bestätigen.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleAccept}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                data-testid="avv-banner-accept"
              >
                {saving ? "Speichern…" : "AVV akzeptieren"}
              </button>
              <a
                href="/avv"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="avv-banner-read"
              >
                <ExternalLink className="h-3 w-3" /> Lesen
              </a>
              <button
                onClick={handleLater}
                className="ml-auto text-xs text-gray-500 hover:text-gray-700 px-2"
                data-testid="avv-banner-later"
              >
                Später
              </button>
            </div>
          </div>
          <button
            onClick={handleLater}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 -mt-1 -mr-1"
            aria-label="Schließen"
            data-testid="avv-banner-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
